import { DateTime } from 'luxon';

import { DEFAULT_CATEGORY_LABELS } from '../categories';
import { floorToSlot } from '../event-time';
import { ParseResult } from './event-details-parser';
import { FilledBy } from './voice-merge';

/**
 * "Plan it with me": the create form asked as a conversation, one field at a
 * time. Pure logic only — what to ask next, the words, the quick answers, and
 * how a short answer maps onto a field. The component does the talking and
 * listening; the form keeps doing the filling (voice-merge rules and all).
 */

export type ChatStep = 'what' | 'kind' | 'name' | 'when' | 'long' | 'host' | 'cover' | 'recap';

/** What the conversation needs to know about the form right now. */
export interface ChatFormState {
  category: string;
  name: string;
  hasCover: boolean;
  start: DateTime | null;
  end: DateTime | null;
  /** Still on the "starts now" seed. */
  startIsNow: boolean;
  filledBy: FilledBy;
}

/** The form, as the conversation sees it. CreateEventFormComponent implements this. */
export interface ChatHost {
  /** The account's name, "" before sign-in. */
  readonly chatAccountName: string;
  chatState(): ChatFormState;
  /** Fills fields from an answer. Same merge rules as "Say it instead". */
  applyChatAnswer(result: ParseResult, text: string, input: 'voice' | 'chip' | 'typed'): void;
  setChatCover(dataUrl: string): void;
  /** Opens the normal picker; resolves once it has closed. */
  openChatSheet(sheet: 'start' | 'duration' | 'cover'): Promise<void>;
  /** The field the conversation is asking about, to ring and scroll to. */
  chatAsking(step: ChatStep | null): void;
  chatActiveChange(active: boolean): void;
  submitFromChat(): void;
}

export interface ChatChip {
  label: string;
  /** What the chip means, per step. */
  apply: Partial<ParseResult> | 'skip' | 'other' | 'sheet';
}

export const emptyResult = (): ParseResult =>
  ({ eventName: null, category: null, start: null, durationMinutes: null, hostedBy: null, hinglish: false });

// ── what to ask next ─────────────────────────────────────────────────────────

/**
 * The first thing still missing. Anything already answered — in this chat,
 * by an earlier voice fill, or typed by the host — is skipped.
 */
export function nextStep(s: ChatFormState, done: Set<ChatStep>): ChatStep {
  // "Something else" on the first question moves on to "What kind of event is it?".
  if (!s.category && !s.name) return done.has('what') ? 'kind' : 'what';
  if (!s.category) return 'kind';
  if (!s.name) return 'name';
  if (!done.has('when') && s.filledBy.start === 'default') return 'when';
  if (!done.has('long') && s.filledBy.duration === 'default') return 'long';
  if (!done.has('host') && s.filledBy.hostedBy === 'default') return 'host';
  if (!done.has('cover') && !s.hasCover) return 'cover';
  return 'recap';
}

// ── the words ────────────────────────────────────────────────────────────────

const article = (word: string) => (/^[aeiou]/i.test(word) ? 'an' : 'a');

export function question(step: ChatStep, s: ChatFormState, account: string, justAnswered: boolean): string {
  switch (step) {
    case 'what': return 'Hi! What are we celebrating?';
    case 'kind': return 'What kind of event is it?';
    case 'name': {
      const c = s.category.toLowerCase();
      return c ? `Lovely, ${article(c)} ${c}! What should we call it?` : 'What should we call it?';
    }
    case 'when': return justAnswered ? 'Love it. When does it start?' : 'When does it start?';
    case 'long': return 'And how long will it run?';
    case 'host': return account ? `Who's hosting? I've put you down as ${account}.` : 'Who’s hosting? You can skip this.';
    case 'cover': return 'Now the fun part. Pick a cover photo. It’s the first thing guests see.';
    case 'recap': return recapLine(s);
  }
}

/** "Anaya's 5th Birthday, Saturday at 3 PM, until 7 PM. Shall I create it?" */
export function recapLine(s: ChatFormState): string {
  const name = s.name || 'Your event';
  if (!s.start) return `${name}. Shall I create it?`;
  const when = s.startIsNow ? 'starting now' : `${spokenDay(s.start)} at ${spokenTime(s.start)}`;
  const until = s.end ? `, until ${s.end.hasSame(s.start, 'day') ? spokenTime(s.end) : `${spokenDay(s.end)} at ${spokenTime(s.end)}`}` : '';
  return `${name}, ${when}${until}. Shall I create it?`;
}

/** Said again when an answer didn't fit the question. */
export function retryLine(step: ChatStep): string {
  switch (step) {
    case 'when': return 'Sorry, I didn’t catch a day or time. When does it start?';
    case 'long': return 'How long will it run? You can say “until 7” or “4 hours”.';
    case 'cover': return 'Tap the one you like, or say “the first one”.';
    case 'recap': return 'Shall I create it? Say yes, or tap Create event.';
    default: return 'Sorry, I didn’t catch that. Try again, or tap an answer.';
  }
}

function spokenDay(d: DateTime): string {
  const now = DateTime.now();
  if (d.hasSame(now, 'day')) return 'today';
  if (d.hasSame(now.plus({ days: 1 }), 'day')) return 'tomorrow';
  if (d.diff(now.startOf('day'), 'days').days < 7) return d.toFormat('cccc');
  return d.toFormat('cccc d LLLL');
}

function spokenTime(d: DateTime): string {
  return d.minute ? d.toFormat('h:mm a') : d.toFormat('h a');
}

// ── quick answers ────────────────────────────────────────────────────────────

const cat = (label: string): ChatChip => ({ label, apply: { category: { value: label, confidence: 'high', isNew: false } } });
const starts = (label: string, dt: DateTime): ChatChip => ({
  label, apply: { start: { value: { date: dt.toFormat('yyyy-MM-dd'), time: floorToSlot(dt) }, confidence: 'high' } },
});
const lasts = (label: string, hours: number): ChatChip => ({ label, apply: { durationMinutes: { value: hours * 60, confidence: 'high' } } });

export function chipsFor(step: ChatStep, account: string, now: DateTime = DateTime.now()): ChatChip[] {
  switch (step) {
    case 'what':
      return [...DEFAULT_CATEGORY_LABELS.map(cat), { label: 'Something else', apply: 'other' }];
    // "Something else": the host says or types it. The typing field is already open for this step.
    case 'kind':
      return [];
    case 'when': {
      const at = (d: DateTime, h: number) => d.set({ hour: h, minute: 0, second: 0, millisecond: 0 });
      const tomorrow = at(now.plus({ days: 1 }), 19);
      // The coming Saturday; next week's if today is Saturday.
      const sat = at(now.plus({ days: ((6 - now.weekday + 7) % 7) || 7 }), 19);
      const chips = [starts('Now', now), starts('Tomorrow, 7 PM', tomorrow)];
      if (!sat.hasSame(tomorrow, 'day')) chips.push(starts(`${sat.toFormat('ccc d LLL')}, 7 PM`, sat));
      chips.push({ label: 'Pick a date', apply: 'sheet' });
      return chips;
    }
    case 'long':
      return [lasts('2 hours', 2), lasts('4 hours', 4), lasts('8 hours', 8), lasts('1 day', 24), lasts('3 days', 72),
        { label: 'Pick an end', apply: 'sheet' }];
    case 'host':
      return account
        ? [{ label: 'Just me', apply: 'skip' }]
        : [{ label: 'Skip', apply: 'skip' }];
    default:
      return [];
  }
}

// ── reading a short answer ───────────────────────────────────────────────────

const LEAD_FILLER = /^(?:um+|uh+|so|ok(?:ay)?|well|yeah|hmm+|let'?s call it|let us call it|call it|we'?ll call it|i'?ll call it|it'?s called|it is called|the name is|name it|we'?re calling it|it'?s|it is|maybe)[\s,]+/i;

/** "um, let's call it anaya's fifth." → "anaya's fifth" */
export function stripLead(text: string): string {
  let t = text.trim().replace(/[.!?]+$/, '');
  for (let i = 0; i < 3 && LEAD_FILLER.test(t); i++) t = t.replace(LEAD_FILLER, '');
  return t.trim();
}

/** Every word capitalised, apostrophes and small words kept: "anaya's fifth birthday" → "Anaya's Fifth Birthday". */
export function titleCase(s: string): string {
  const small = new Set(['of', 'the', 'and', 'at', 'in', 'on', 'for', 'a', 'an', 'to', 'with', 'by']);
  return s.split(/\s+/).filter(Boolean)
    .map((w, i) => (i > 0 && small.has(w.toLowerCase()) ? w.toLowerCase() : w.charAt(0).toUpperCase() + w.slice(1)))
    .join(' ');
}

/** "me and Priya" → "Sam & Priya"; "just me" → "Sam". */
export function resolveHosts(text: string, account: string): string {
  const t = stripLead(text)
    .replace(/^(?:hosted by|the hosts? (?:are|is)|hosts? (?:are|is)|it's|it is)\s+/i, '')
    .replace(/^(?:we are|we're)$/i, 'we')
    .replace(/^just\s+/i, '');
  const me = /^(?:me|i|myself|mine|us|we)$/i;
  const names: string[] = [];
  for (const part of t.split(/\s*(?:,|&|\band\b|\bplus\b|\baur\b)\s*/i)) {
    const p = part.trim();
    if (!p) continue;
    const name = me.test(p) ? account : titleCase(p);
    if (name && !names.includes(name)) names.push(name);
  }
  return names.join(' & ').slice(0, 50);
}

/** "the second one" → 1, "skip" → 'skip', "my own" → 'upload'. */
export function pickFromSpeech(text: string): number | 'skip' | 'upload' | null {
  const t = text.toLowerCase();
  if (/\b(skip|no cover|none|not now|later|no thanks|nahi)\b/.test(t)) return 'skip';
  if (/\b(upload|my own|own photo|my photo|from my phone|gallery|camera roll)\b/.test(t)) return 'upload';
  // "one" is in "the last one" too, so the bare number only counts said alone.
  if (/\b(third|3rd|three|last|right)\b/.test(t)) return 2;
  if (/\b(second|2nd|two|middle)\b/.test(t)) return 1;
  if (/\b(first|1st|left)\b/.test(t) || /^\s*(?:the\s+)?(?:number\s+)?one\s*$/.test(t)) return 0;
  return null;
}

/** "right now", "it's already started" — an answer to "when does it start?". */
export function meansNow(text: string): boolean {
  return /\b(?:right now|now|straight away|immediately|already started|started already|abhi)\b/i.test(text);
}

/**
 * "until 7", "till about 11 pm", "to midnight" — an answer to "how long will it
 * run?" measured from the start we already have. A bare hour is the next time
 * it comes round after the start (a 3 PM start "until 7" ends at 7 PM; an 8 PM
 * start "until 1" ends at 1 AM). Null when it isn't an end time.
 */
export function untilMinutes(start: DateTime, text: string): number | null {
  const t = text.toLowerCase().replace(/\./g, '');
  const m = t.match(/\b(?:until|till|til|to|up to|upto|ends? at|finish(?:es)? at|tak)\s+(?:about\s+|around\s+|roughly\s+)?(?:(midnight|noon|midday)|(\d{1,2})(?::(\d{2}))?\s*(am|pm)?)\b/);
  if (!m) return null;

  let hour: number;
  let minute = 0;
  let meridian = m[5] as 'am' | 'pm' | undefined;
  if (m[1]) {
    hour = m[1] === 'midnight' ? 0 : 12;
    meridian = m[1] === 'midnight' ? 'am' : 'pm';
  } else {
    hour = parseInt(m[2], 10);
    minute = m[3] ? parseInt(m[3], 10) : 0;
    if (hour > 23 || minute > 59) return null;
  }

  const at = (h24: number, dayOffset: number) =>
    start.startOf('day').plus({ days: dayOffset }).set({ hour: h24, minute });
  const candidates: DateTime[] = [];
  const hours24 = meridian
    ? [meridian === 'pm' ? (hour % 12) + 12 : hour % 12]
    : hour > 12 ? [hour] : [hour % 12, (hour % 12) + 12];
  for (const d of [0, 1]) for (const h of hours24) candidates.push(at(h, d));
  const end = candidates.filter(c => c > start).sort((a, b) => a.toMillis() - b.toMillis())[0];
  if (!end) return null;

  const minutes = Math.round(end.diff(start, 'minutes').minutes);
  return minutes >= 15 && minutes <= 10 * 24 * 60 ? minutes : null;
}

/** For "Shall I create it?". */
export function yesNo(text: string): 'yes' | 'no' | null {
  const t = text.toLowerCase().trim();
  if (/^(no|nope|nah|wait|not yet)\b|\b(change|edit|fix)\b/.test(t)) return 'no';
  if (/\b(yes|yeah|yep|yup|sure|go ahead|create|do it|ok|okay|perfect|looks good|sounds good|haan|han ji|ji)\b/.test(t)) return 'yes';
  return null;
}

/**
 * "until 7" / "till midnight" as the answer to "how long": the parser reads it
 * as a start time, so turn that into a length from the start we already have.
 */
export function minutesUntil(start: DateTime, parsed: ParseResult): number | null {
  const time = parsed.start?.value.time;
  if (!time) return null;
  const m = time.match(/^(\d{1,2}):(\d{2}) (AM|PM)$/);
  if (!m) return null;
  let hour = parseInt(m[1], 10) % 12;
  if (m[3] === 'PM') hour += 12;
  let end = start.set({ hour, minute: parseInt(m[2], 10) });
  if (end <= start) end = end.plus({ days: 1 });
  const minutes = Math.round(end.diff(start, 'minutes').minutes);
  return minutes >= 15 && minutes <= 10 * 24 * 60 ? minutes : null;
}
